"use client";

import { useCallback, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useRememberedState } from "@/components/admin/AdminListMemory";
import { rememberedCount } from "@/lib/adminListMemory";

/**
 * Jumlah kartu yang ditampilkan per "halaman" di board admin. Kartu TIDAK
 * dirender semua sekaligus di awal — hanya 20 pertama, lalu bertambah 20 tiap
 * kali pengguna menggulir mendekati ujung daftar (infinite scroll).
 */
export const BOARD_PAGE_SIZE = 20;

/**
 * Pasang IntersectionObserver pada sebuah elemen "sentinel". `onReachEnd`
 * dipanggil saat sentinel masuk viewport (dengan margin 320px supaya memuat
 * lebih awal). `enabled=false` mematikan pemicu tanpa melepas node.
 *
 * Mengembalikan callback-ref: tempelkan ke elemen sentinel. Observer dipasang
 * ulang otomatis saat node mount/unmount — cocok untuk sentinel yang dirender
 * kondisional (hanya saat masih ada data berikutnya).
 */
export function useInfiniteSentinel(
  onReachEnd: () => void,
  enabled: boolean
): (node: HTMLDivElement | null) => void {
  // Simpan callback & flag di ref supaya observer (dipasang sekali per node)
  // selalu membaca nilai terbaru tanpa perlu dibuat ulang tiap render.
  const cbRef = useRef(onReachEnd);
  cbRef.current = onReachEnd;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const observerRef = useRef<IntersectionObserver | null>(null);

  return useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && enabledRef.current) cbRef.current();
      },
      { rootMargin: "320px" }
    );
    obs.observe(node);
    observerRef.current = obs;
  }, []);
}

/**
 * Windowing sisi-klien untuk daftar yang sudah dimuat penuh: render hanya
 * `pageSize` item pertama, tambah `pageSize` lagi tiap sentinel tergulir ke
 * viewport. Hitungan dikembalikan ke halaman pertama saat `resetKey` berubah
 * (mis. kata kunci pencarian / arah urut / scope berganti) — reset dilakukan
 * saat render (pola "adjust state on prop change") agar bebas kedip.
 *
 * `memoryField` = nama field memori daftar (`AdminListMemory`): jumlah kartu
 * yang sudah terbuka ikut dipulihkan saat Kembali, supaya kartu jangkar
 * posisi scroll benar-benar dirender. `resetKey` awal sudah memakai state
 * pulihan (kata kunci dll.), jadi tak ada reset yang membuangnya.
 */
export function useRevealOnScroll<T>(
  items: T[],
  resetKey: string,
  pageSize: number = BOARD_PAGE_SIZE,
  memoryField: string | null = null
): {
  visible: T[];
  hasMore: boolean;
  sentinelRef: (node: HTMLDivElement | null) => void;
  /** Jumlah kartu yang sudah dibuka (boleh melebihi `items.length`). */
  count: number;
} {
  const [count, setCount] = useRememberedState(memoryField, pageSize, (raw) =>
    rememberedCount(raw, pageSize)
  );
  const [prevKey, setPrevKey] = useState(resetKey);
  if (prevKey !== resetKey) {
    setPrevKey(resetKey);
    setCount(pageSize);
  }

  const hasMore = count < items.length;
  const sentinelRef = useInfiniteSentinel(
    () => setCount((c) => c + pageSize),
    hasMore
  );

  return { visible: items.slice(0, count), hasMore, sentinelRef, count };
}

/**
 * Penanda ujung daftar: elemen sentinel (target IntersectionObserver) plus
 * spinner kecil sebagai isyarat "masih ada lagi di bawah". Dirender hanya saat
 * masih ada halaman berikutnya.
 */
export function LoadMoreSentinel({
  sentinelRef
}: {
  sentinelRef: (node: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={sentinelRef}
      // lg:col-span-2: bila daftar induk jadi grid 2 kolom di desktop,
      // sentinel tetap satu baris penuh (di luar grid kelas ini tanpa efek).
      className="flex items-center justify-center py-5 lg:col-span-2"
      aria-hidden
    >
      <Loader2 className="h-5 w-5 animate-spin text-muted" />
    </div>
  );
}
